import { ChevronDown, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { useTenant } from '@/contexts/tenant-context';
import { useState } from 'react';

export function TenantSelector({ onCreateClick }: { onCreateClick: () => void }) {
  const { currentTenant, tenants, isLoading, setCurrentTenant } = useTenant();
  const [open, setOpen] = useState(false);

  const handleCreateClick = () => {
    setOpen(false);
    onCreateClick();
  };

  if (isLoading) {
    return <Skeleton className="h-6 w-[180px]" />;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          role="combobox"
          aria-expanded={open}
          className="flex items-center gap-2 cursor-pointer text-sm hover:text-primary transition-colors"
        >
          {currentTenant ? (
            <div className="flex items-center gap-1.5">
              <div className="h-5 w-5 rounded-full bg-primary-blue-20 flex items-center justify-center text-xs font-medium text-primary-blue-100">
                {currentTenant.name.charAt(0).toUpperCase()}
              </div>
              <span className="truncate max-w-[150px]">{currentTenant.name}</span>
            </div>
          ) : (
            'Select tenant'
          )}
          <ChevronDown
            className="h-4 w-4 opacity-70 transition-transform"
            style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0 mt-2">
        <Command>
          <CommandInput placeholder="Search tenant..." />
          <CommandList>
            <CommandEmpty>No tenant found.</CommandEmpty>
            <CommandGroup heading="Your Tenants">
              {tenants.map((tenant, idx) => (
                <CommandItem
                  key={tenant.id}
                  value={tenant.id}
                  onSelect={() => {
                    setCurrentTenant(tenant);
                    setOpen(false);
                  }}
                  className={cn('mt-1 cursor-pointer', { 'bg-accent text-primary': currentTenant?.id === tenant.id })}
                >
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-5 rounded-full bg-primary-blue-20 flex items-center justify-center text-xs font-medium text-primary-blue-100">
                      {tenant.name.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <span
                    className={cn('truncate max-w-[150px] text-sm hover:text-primary', {
                      'text-primary': currentTenant?.id === tenant.id,
                    })}
                  >
                    {tenant.name}
                  </span>
                  <CommandShortcut>⌘{idx + 1}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>
              <CommandItem onSelect={handleCreateClick} className="text-primary">
                <Plus className="mr-2 h-4 w-4" />
                Create Tenant
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
